/* eslint-disable no-underscore-dangle */
import { useEffect, useState } from 'react';
import { nanoid } from 'nanoid';
import { useSearchZustandStore } from '@/helpers/searchctx.js';
import { createHTMLMapMarker } from './marker.js';
import StoreMarkers from './store.js';
import Emitter from '@/helpers/emitter.js';
import { getNewBounds } from '@/helpers/position.js';

function MapBuilder() {
  const mapDataResults = useSearchZustandStore((state) => state.mapDataResults);
  const pinData = useSearchZustandStore((state) => state.pinData);
  const spaceHoverData = useSearchZustandStore((state) => state.spaceHoverData);

  const { map, markerCluster, polygons } = useSearchZustandStore.getState().mapData;
  const { mapOutput } = useSearchZustandStore.getState();
  const [storeMarkers] = useState(new StoreMarkers());
  const [clusterOpen, setClusterOpen] = useState(null);

  function consumerMoveMap({
    lat, lng, zoom, latlng, hub, title, viewport,
  }) {
    let newZoom = zoom;
    if (viewport) {
      const { mapRef } = useSearchZustandStore.getState();
      const dimMapRef = mapRef.current.getBoundingClientRect();
      const newBounds = getNewBounds({
        bounds: {
          ne: {
            lat: viewport.northeast.lat,
            lng: viewport.northeast.lng,
          },
          sw: {
            lat: viewport.southwest.lat,
            lng: viewport.southwest.lng,
          },
        },
        width: dimMapRef.width,
        height: dimMapRef.height,
      });

      if (newBounds?.zoom) {
        newBounds.zoom -= 1;
        if (newBounds?.zoom === map.getZoom()) newZoom = 15;
        else newZoom = newBounds?.zoom;
      }
      // console.log('consumerMoveMap newBounds', newBounds);
    }

    // console.log('consumerMoveMap', {
    //   lat, lng, zoom, latlng, hub, title,
    // });

    // TODO hub, title
    /* eslint-disable-next-line no-undef */
    if (lat && lng) map.panTo(new google.maps.LatLng(lat, lng));
    if (latlng) map.panTo(latlng);
    if (newZoom) map.setZoom(newZoom);
    if (hub && title) {
      const { initParams } = useSearchZustandStore.getState();
      useSearchZustandStore.setState({ initParams: { ...initParams, title, hub } });
    }
    // trigger will call search
    /* eslint-disable-next-line no-undef */
    if (google) google.maps.event.trigger(map, 'dragend', {});
  }

  function cleanMarkers(markers, all) {
    if (!map || !markerCluster || !markers?.length) return [];
    let newMarkers = []; const
      markersDelete = [];
    // console.log('map build - clean markers', all);
    const mbounds = map.getBounds();
    if (mbounds && markers?.length) {
      markers.forEach((m) => {
        if (all || !mbounds.contains(m.getPosition())) {
          m.setMap(null);
          m.remove();
          markersDelete.push(m);
        } else {
          newMarkers.push(m);
        }
      });
    }

    if (clusterOpen) {
      if (mbounds && !mbounds.contains(clusterOpen.getPosition())) {
        // console.log('map build - close opened marker');
        clusterOpen.remove();
        setClusterOpen(null);
      }
    }
    markerCluster.removeMarkers(markersDelete);

    if (all) {
      newMarkers = [];
    }

    return newMarkers;
  }

  function closeMarkerOpen() {
    if (clusterOpen) {
      // console.log('map build - close opened marker', clusterOpen);
      clusterOpen.remove();
      setClusterOpen(null);
    }
  }

  function consumerCleanAllMarkers(allMarkers) {
    // console.log('map build - CLEAR ALL MARKERS', allMarkers);
    cleanMarkers(storeMarkers.getMarkers(), allMarkers);
    cleanMarkers(storeMarkers.getClustering(), allMarkers);
    storeMarkers.reset();
    closeMarkerOpen();
  }

  function handleNoResults() {
    const currentZoom = map.getZoom();
    if (currentZoom > 10) { // On ne dézoome pas en dessous de 10 pour éviter de trop s'éloigner
      const newZoom = Math.max(10, currentZoom - 1); // On revient à un dézoom de 1 niveau
      map.setZoom(newZoom);
      // Déclencher une nouvelle recherche avec le nouveau zoom
      useSearchZustandStore.setState({
        searchEvent: {
          action: 'mapidlecall',
          data: {
            lat: map.getCenter().lat(),
            lng: map.getCenter().lng(),
            zoom: newZoom,
            boundaries: [
              [map.getBounds().getSouthWest().lat(), map.getBounds().getSouthWest().lng()],
              [map.getBounds().getNorthEast().lat(), map.getBounds().getNorthEast().lng()],
            ],
          },
        },
      });
    }
  }

  function consumerHoverPolygons(dataPolygon) {
    const { polygons: polygonsconsumer } = useSearchZustandStore.getState().mapData;
    const polygon = polygonsconsumer.find((p) => p.slug === dataPolygon.slug);
    /* eslint-disable-next-line no-undef */
    if (dataPolygon.isHover) google.maps.event.trigger(polygon.item, 'mouseover', {});
    /* eslint-disable-next-line no-undef */
    else google.maps.event.trigger(polygon.item, 'mouseout', {});
  }
  function consumerHoverSpace(dataSpace) {
    const { markerCluster: markerClusterconsumer } = useSearchZustandStore.getState().mapData;
    if (!markerClusterconsumer) return;
    (markerClusterconsumer.getClusters() || []).forEach((c) => {
      if (c?.clusterIcon_?.div_?.style) c.clusterIcon_.div_.classList.remove('cluster-hover');
    });
    if (dataSpace?.ref && dataSpace?.value) {
      const cluster = (markerClusterconsumer.getClusters() || []).find((cluster) => {
        const marker = (cluster.getMarkers() || []).find((marker) => (
          marker.markerprops.iid === dataSpace.ref
        ));
        return !!marker;
      });
      if (cluster?.clusterIcon_?.div_?.style) {
        cluster.clusterIcon_.div_.classList.add('cluster-hover');
      }
    }
  }

  // GENERATE SPACE AND LET CLUSTERING
  function generateMarkerBySpace(markers, spaces) {
    const oldMarkers = [...markers];
    const newMarkers = [];
    spaces.forEach((space) => {
      // eslint-disable-next-line max-len, no-undef
      const latlng = new google.maps.LatLng(space.searchMap.location[1], space.searchMap.location[0]);
      const markerExist = oldMarkers.find((m) => m.markerprops.iid === space.id);
      if (markerExist) {
        // console.log('map build - marker by space exist');
        return;
      }
      const marker = createHTMLMapMarker({
        latlng,
        map,
        style: {
          zIndex: 10,
        },
        markerprops: {
          key: `marker-${space.id}`,
          iid: space.id,
          text: `${Math.round(space?.dataByCategory?.price?.amount_excl_tax)}€`,
          icon: null,
          classes: '',
          bySpace: true,
        },
      });
      newMarkers.push(marker);
    });

    // console.log('map build - generate markers by space', oldMarkers.length, newMarkers.length);

    markerCluster.addMarkers(newMarkers);
    newMarkers.forEach((marker) => marker.setMap(map));

    return [...oldMarkers, ...newMarkers] || [];
  }

  // GENERATE CITY AND DISTRICT BY SPACE COUNTING
  function generateMarkerBySpaceCounting({ newMarkers, listDatas, isDistrict }) {
    // console.log('map build - marker counting');
    const isPolygonsOn = {};
    listDatas.forEach((bz) => {
      // only district
      if (mapOutput.zoom <= 13 && bz?.location && (bz?.spacesCount || bz?.spaces?.length)) {
        // eslint-disable-next-line no-undef
        const latlng = new google.maps.LatLng(bz.location[1], bz.location[0]);
        const markerExist = newMarkers.find((m) => m.markerprops.iid === bz.id);
        isPolygonsOn[bz.id] = 'OK';
        if (markerExist) {
          // console.log('map build - counting exist');
          return;
        }

        const marker = createHTMLMapMarker({
          latlng,
          // map: map,
          style: {
            zIndex: 10,
          },
          markerprops: {
            key: `marker-${bz.id}`,
            // userref : s.user,
            iid: bz.id,
            text: bz?.spacesCount || bz?.spaces?.length,
            icon: null,
            spacesCount: bz?.spacesCount || bz?.spaces?.length,
            isDistrict,
            classes: '',
            // onClick: (id, type) => {
            // console.log('self.props.onClick', id,type)
            // },
            // category: () => {
            // console.log('self.props.category');
            // },
          },
        // onMouveOver: (obj, isOver) => {
        // console.log(`searchresultsspaceconsumerdistricthover${obj?.markerprops?.iid}`, isOver)
        // if (!isOver){
        // Emitter.emit(`searchresultsspaceconsumerdistricthover${obj?.markerprops?.iid}`, false)
        // } else if (obj?.markerprops?.iid && isOver) {
        // Emitter.emit(`searchresultsspaceconsumerdistricthover${obj?.markerprops?.iid}`, true)
        // }
        // },
        });
        newMarkers.push(marker);
      }
    });

    polygons.forEach((p) => {
      if (isPolygonsOn[p.slug]) {
        p.item.setMap(map);
      } else {
        p.item.setMap(null);
      }
    });

    newMarkers.forEach((marker) => marker.setMap(map));
    return newMarkers || [];
  }

  useEffect(() => {
    Emitter.on('search_map_build_clearAllMarkers', consumerCleanAllMarkers);
    Emitter.on('search_map_build_moveMap', consumerMoveMap);
    Emitter.on('searchresultsspacehoversignalemittopolygons', consumerHoverPolygons);
    Emitter.on('searchresultsspacehoversignalemitcluster', consumerHoverSpace);

    return () => {
      Emitter.off('search_map_build_clearAllMarkers', consumerCleanAllMarkers);
      Emitter.off('search_map_build_moveMap', consumerMoveMap);
      Emitter.off('searchresultsspacehoversignalemittopolygons', consumerHoverPolygons);
    };
  }, [mapDataResults, pinData, spaceHoverData]);

  // // MARKERCLUSTER CLICK
  useEffect(() => {
    // console.log('map build - marker open', clusterOpen, pinData);
    if (pinData) {
      if (pinData?.caller?.match(/^clusterclick/) && pinData?.latlng && pinData?.spaces?.length) {
        // console.log('map build, cluster click', pinData);
        clusterOpen?.remove();
        const { latlng, spaces } = pinData;
        const marker = createHTMLMapMarker({
          latlng,
          map,
          style: {
            zIndex: 20,
          },
          markerprops: {
            pinId: nanoid(),
            iid: `opengroupSpace_${nanoid()}`,
            openSpaces: true,
            spaces,
            fromCluster: true,
          },
          // onMouveOver: (value) => {
          // self.parent.markerMouseOver = value
          // },
        });
        marker.setMap(map);
        setClusterOpen(marker);
        useSearchZustandStore.setState({ pinInOpen: nanoid() });
      } else {
        closeMarkerOpen();
      }
    } else {
      closeMarkerOpen();
    }
  }, [pinData]);

  // SEARCH EVENT FILL MAPDATA
  useEffect(() => {
    // console.log('map build - useeffect call mapDataResults');

    if (map && markerCluster) {
      let newMarkers = cleanMarkers(storeMarkers.getMarkers());
      let newMarkersClustering = cleanMarkers(storeMarkers.getClustering());

      // Vérifier s'il y a des résultats
      const hasResults = mapDataResults?.results?.spaces?.length > 0 ||
                         mapDataResults?.results?.byCity?.length > 0 ||
                         mapDataResults?.results?.byDistrict?.length > 0;

      if (!hasResults && mapOutput.zoom > 13) {
        // Si aucun résultat n'est trouvé et qu'on est zoomé, on dézoome
        handleNoResults();
        return;
      }

      if (mapOutput.zoom <= 13) {
        newMarkers = [...(generateMarkerBySpaceCounting({
          newMarkers,
          listDatas: mapDataResults?.results?.byCity || [],
        }))];
      }
      if (mapOutput.zoom >= 11) {
        newMarkers = [...(generateMarkerBySpaceCounting({
          newMarkers,
          listDatas: mapDataResults?.results?.byDistrict || [],
          isDistrict: true,
        }))];
      }
      if (mapOutput.zoom > 13) {
        // eslint-disable-next-line max-len
        newMarkersClustering = [...(generateMarkerBySpace(newMarkersClustering, mapDataResults?.results?.spaces || []))];
      }

      // console.log('map build - new markers, clustering', newMarkers.length, newMarkersClustering.length);
      storeMarkers.setMarkers(newMarkers);
      storeMarkers.setClustering(newMarkersClustering);
    }
  }, [mapDataResults?.results]);

  // RESULT SPACE HOVER

  useEffect(() => {
    const { clustersObject } = useSearchZustandStore.getState();
    if (map && markerCluster && Object.entries(clustersObject).length) {
      try {
        let isCheckMarkers = true;
        if (mapOutput.zoom <= 13 && polygons) {
          (polygons || []).forEach((p) => {
            if (spaceHoverData?.locationSlug === p?.item?._slugGZ) {
              isCheckMarkers = false;
              // eslint-disable-next-line no-undef
              google.maps.event.trigger(p?.item, 'mouseover', { stop: 0, vertex: 0 });
            } else {
              // eslint-disable-next-line no-undef
              google.maps.event.trigger(p?.item, 'mouseout', { stop: 0, vertex: 0 });
            }
          });
        } else if (mapOutput.zoom > 13) {
          let clusterExist = false;
          ((clustersObject && clustersObject?.getClusters()) || []).forEach((c) => {
            let markerExist = false;
            (c.getMarkers() || []).forEach((m) => {
              if (spaceHoverData && m?.markerprops?.iid === spaceHoverData?.ref) {
                // eslint-disable-next-line no-undef
                google.maps.event.trigger(m.div, 'mouseover', { stop: 0, vertex: 0 });
                markerExist = true;
                clusterExist = true;
              } else {
              // eslint-disable-next-line no-undef
                google.maps.event.trigger(m.div, 'mouseout', { stop: 0, vertex: 0 });
              }
            });
            if (markerExist && c.getMarkers().length > 1) {
              // eslint-disable-next-line no-param-reassign
              if (c?.clusterIcon_?.div_?.style) c.clusterIcon_.div_.style.border = '';
              // eslint-disable-next-line no-param-reassign
            } else if (c?.clusterIcon_?.div_?.style) c.clusterIcon_.div_.style.border = '';
          });
          if (!clusterExist) isCheckMarkers = true;
        }

        // no polygon, check city
        if (spaceHoverData && isCheckMarkers === true) {
          (storeMarkers.getMarkers() || []).forEach((m) => {
            if (m?.markerprops?.iid === spaceHoverData?.locationPlaceId) {
              // eslint-disable-next-line no-undef
              google.maps.event.trigger(m.div, 'mouseover', { stop: 0, vertex: 0 });
            } else {
              // eslint-disable-next-line no-undef
              google.maps.event.trigger(m.div, 'mouseout', { stop: 0, vertex: 0 });
            }
          });
        }
      } catch (e) {
        console.log(e);
      }
    }
  }, [spaceHoverData]);

  return null;
}

export default MapBuilder;
